import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  IconButton,
  Collapse,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

// Define colors used in SimulationOverlay (ensure these match)
const COLOR_WATER = "rgba(65, 105, 225, 0.9)";
const COLOR_BURNING = "rgba(255, 0, 0, 0.9)";
const COLOR_SMOLDERING = "rgba(170, 80, 0, 0.8)";
const COLOR_INFRASTRUCTURE = "rgba(160, 160, 160, 0.9)"; // Representative grey
const COLOR_TREE = "rgba(0, 128, 0, 0.85)"; // Representative green
const COLOR_RETARDANT = "rgba(255, 105, 180, 0.6)";
const COLOR_DIGGING = "rgba(139, 69, 19, 0.7)";
export const STATION_COLOR = "rgba(0, 180, 255, 0.9)"; // From DroneGrid - Added export

const legendItems = [
  { color: COLOR_TREE, label: "Vegetation / Forest" },
  { color: COLOR_INFRASTRUCTURE, label: "Infrastructure" },
  { color: COLOR_WATER, label: "Water" },
  { color: COLOR_BURNING, label: "Burning" },
  { color: COLOR_SMOLDERING, label: "Smoldering" },
  { color: COLOR_RETARDANT, label: "Retardant Applied" },
  { color: COLOR_DIGGING, label: "Digging Firebreak" },
  { color: STATION_COLOR, label: "Drone Station" },
  // Add other states if needed, e.g., Burnt Infrastructure
];

const LegendItem = ({ color, label }) => (
  <Grid item xs={6} sm={12} display="flex" alignItems="center" mb={0.5}>
    <Box
      sx={{
        width: 14,
        height: 14,
        backgroundColor: color,
        border: "1px solid rgba(255, 255, 255, 0.3)",
        mr: 1,
        flexShrink: 0,
      }}
    />
    <Typography variant="caption" sx={{ lineHeight: 1.2 }}>
      {label}
    </Typography>
  </Grid>
);

const Legend = (props) => {
  // Receive sx props from App.js
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [isOpen, setIsOpen] = useState(false);

  const handleToggle = (event) => {
    event.stopPropagation(); // Prevent click from propagating to map if legend is on map
    setIsOpen(!isOpen);
  };

  return (
    // Apply sx props passed down from App.js for positioning/responsive styles
    <Paper
      elevation={4}
      sx={{
        p: { xs: 0.5, sm: 1.5 }, // Responsive padding
        backgroundColor: "rgba(40, 40, 40, 0.85)",
        backdropFilter: "blur(4px)",
        borderRadius: 2,
        maxWidth: { xs: "40px", sm: "180px" }, // Collapse width on mobile unless open
        minWidth: "40px", // Ensure button is visible
        maxHeight: { xs: "40px", sm: "none" }, // Collapse height on mobile unless open
        overflow: "hidden", // Hide content when collapsed
        zIndex: 10,
        pointerEvents: "auto", // Enable pointer events for the button/paper
        transition: "max-width 0.3s ease, max-height 0.3s ease", // Smooth transition
        position: "relative", // Needed for absolute positioning of button
        ...(isOpen && {
          // Styles when open on mobile
          maxWidth: { xs: "180px", sm: "180px" },
          maxHeight: { xs: "300px", sm: "none" }, // Allow height expansion
        }),
        ...props.sx, // Merge with sx props from App.js
      }}
    >
      {/* Toggle Button - Only on Mobile */}
      {isMobile && (
        <IconButton
          onClick={handleToggle}
          size="small"
          sx={{
            position: "absolute",
            top: 2,
            right: 2,
            zIndex: 11, // Above content
            color: "white",
            backgroundColor: "rgba(0,0,0,0.3)",
            "&:hover": {
              backgroundColor: "rgba(0,0,0,0.5)",
            },
            display: { xs: "flex", sm: "none" }, // Show only on xs
          }}
        >
          <InfoOutlinedIcon fontSize="small" />
        </IconButton>
      )}

      {/* Legend Content - Collapsible */}
      <Collapse
        in={!isMobile || isOpen}
        timeout="auto"
        unmountOnExit={!isMobile}
      >
        <Box
          sx={{
            p: isMobile ? 1 : 0,
            pt: isMobile ? 3 : 0 /* Add padding top on mobile when open */,
          }}
        >
          <Typography
            variant="subtitle2"
            gutterBottom
            sx={{
              mb: 1,
              textAlign: "center",
              display: { xs: isOpen ? "block" : "none", sm: "block" }, // Hide title on mobile unless open
            }}
          >
            Legend
          </Typography>
          <Grid container spacing={0.5}>
            {legendItems.map((item) => (
              <LegendItem
                key={item.label}
                color={item.color}
                label={item.label}
              />
            ))}
          </Grid>
        </Box>
      </Collapse>
    </Paper>
  );
};

export default Legend;
